import { cn } from '@/lib/utils';

export type CoverTone = 'forest' | 'ink' | 'slate' | 'clay' | 'brass';

const toneClass: Record<CoverTone, string> = {
  forest: 'bg-[hsl(var(--cover-forest))]',
  ink: 'bg-[hsl(var(--cover-ink))]',
  slate: 'bg-[hsl(var(--cover-slate))]',
  clay: 'bg-[hsl(var(--cover-clay))]',
  brass: 'bg-[hsl(var(--cover-brass))]',
};

type Size = 'sm' | 'md' | 'lg';

const sizeClass: Record<Size, string> = {
  sm: 'w-[84px] sm:w-[72px]',
  md: 'w-[104px] sm:w-[120px]',
  lg: 'w-[70%] max-w-[240px] sm:w-full',
};

const titleClass: Record<Size, string> = {
  sm: 'text-[9px] sm:text-[8px] leading-tight',
  md: 'text-[10px] sm:text-[11px] leading-snug',
  lg: 'text-base md:text-lg leading-snug',
};

interface BookCoverProps {
  title: string;
  tone?: CoverTone;
  size?: Size;
  imprint?: string;
  className?: string;
  hover?: boolean;
}

export const BookCover = ({
  title,
  tone = 'forest',
  size = 'md',
  imprint = 'Modern Nomad Living',
  className,
  hover = true,
}: BookCoverProps) => {
  return (
    <div
      className={cn('relative', sizeClass[size], className)}
      aria-hidden="true"
    >
      {/* ground shadow */}
      <div className="absolute inset-x-3 -bottom-1 h-3 bg-foreground/25 blur-md" />

      <div
        className={cn(
          'relative aspect-[3/4] overflow-hidden shadow-luxe transition-all duration-700 ease-editorial',
          toneClass[tone],
          hover && 'group-hover:-translate-y-1.5 group-hover:-rotate-[0.8deg] group-hover:shadow-luxe-hover'
        )}
      >
        {/* spine */}
        <div className="absolute left-0 top-0 bottom-0 w-[7%] bg-foreground/30" />
        <div className="absolute left-[7%] top-0 bottom-0 w-px bg-[hsl(var(--cover-foil))]/40" />
        <div className="absolute left-[8.5%] top-0 bottom-0 w-px bg-[hsl(var(--cover-foil))]/15" />
        {/* page edge */}
        <div className="absolute right-0 top-[2%] bottom-[2%] w-[3%] bg-[hsl(var(--chalk))]/85" />
        <div className="absolute right-[3%] top-[2%] bottom-[2%] w-px bg-foreground/25" />

        {/* foil border frame */}
        <div className="absolute inset-y-[6%] left-[12%] right-[7%] border border-[hsl(var(--cover-foil))]/15 pointer-events-none" />

        {/* cover content */}
        <div className="absolute inset-0 pl-[14%] pr-[10%] py-[10%] flex flex-col">
          <div className="h-px w-8 bg-[hsl(var(--cover-foil))]/80" />
          <h3
            className={cn(
              'font-serif font-medium text-[hsl(var(--cover-text))] mt-[8%]',
              titleClass[size]
            )}
          >
            {title}
          </h3>
          <div className="mt-auto">
            <div className="h-px w-full bg-[hsl(var(--cover-foil))]/30 mb-[6%]" />
            <p
              className={cn(
                'uppercase tracking-luxury text-[hsl(var(--cover-foil))]/70',
                size === 'lg' ? 'text-[9px]' : 'text-[6px]'
              )}
            >
              {imprint}
            </p>
          </div>
        </div>

        {/* sheen */}
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--cover-text))]/10 via-transparent to-foreground/30 pointer-events-none" />
      </div>

    </div>
  );
};
